import {
  Archive,
  Calendar,
  CalendarCheck,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  CircleDot,
  CircleUserRound,
  Clock,
  ExternalLink,
  Flag,
  FolderOpen,
  Loader2,
  MessageSquare,
  MessageSquareText,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Reply,
  RotateCcw,
  Tag,
  UserRound,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StoplightLoader } from "@/components/LoadingScreen";
import {
  acceptTask,
  addTaskComment,
  archiveTask,
  editTaskComment,
  reopenTask,
  requestChanges,
  setReviewPriority,
  unarchiveTask,
  type ClickUpSubtask,
  type RichDoc,
  type SignoffStatus,
  type TaskDetailData,
} from "@/lib/board";
import { canPrioritize } from "@/lib/perms";
import {
  dropTaskFromBoardColumns,
  invalidateAfterArchiveChange,
  qk,
  subtaskDescriptionOptions,
  taskDetailOptions,
  useUser,
} from "@/lib/queries";
import { handledClickupAuth } from "@/lib/reconnect";
import { DecisionDialogs } from "./taskDetail/DecisionDialogs";
import { InlineComposer } from "./taskDetail/InlineComposer";
import { RequestChangesDialog } from "./taskDetail/RequestChangesDialog";
import { ReviewPriority, ReviewPriorityBadge } from "./taskDetail/ReviewPriority";
import { RichCommentBody } from "./taskDetail/RichCommentBody";
import {
  buildActivity,
  SIGNOFF_DOT,
  type ActivityComment,
  type ActivityEvent,
} from "./taskDetail/activity";
import {
  commentComposerKey,
  daysSince,
  fmtDate,
  fmtDateTime,
} from "./taskDetail/format";
import { ImageThumb } from "./taskDetail/ImageThumb";
import { CommentBody, Description } from "./taskDetail/markdown";
import {
  ClickUpStatusPill,
  Person,
  PRIORITY_FALLBACK,
  Priority,
  Prop,
  SectionHeading,
  STATUS_LABEL,
  STOPLIGHT_SVG,
  TagChip,
} from "./taskDetail/primitives";



const RAIL_FADE_PX = 24;
/**
 * Edge fade for the conversation rail — only the overflowing edge(s) fade, so a
 * short (non-overflowing) thread is left untouched.
 */
function railFadeMask(o: { up: boolean; down: boolean }): string | undefined {
  if (!o.up && !o.down) return undefined;
  const stops = [
    o.up ? "transparent 0" : "black 0",
    o.up ? `black ${RAIL_FADE_PX}px` : "",
    o.down ? `black calc(100% - ${RAIL_FADE_PX}px)` : "",
    o.down ? "transparent 100%" : "black 100%",
  ].filter(Boolean);
  return `linear-gradient(to bottom, ${stops.join(", ")})`;
}

export function TaskDetailDialog({
  taskId,
  canDecide,
  onOpenChange,
}: {
  taskId: string | null;
  canDecide: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const user = useUser();
  const canFlag = canPrioritize(user); // who may set review priority (admin/dev)
  // "accept" → confirm dialog; "request" → the shared RequestChangesDialog
  // (which owns its own feedback/rich/attachment draft state).
  const [action, setAction] = useState<null | "accept" | "request">(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [expandedSub, setExpandedSub] = useState<string | null>(null);
  // Whether the older (non-latest) changes-requested rounds are expanded.
  const [showEarlierFeedback, setShowEarlierFeedback] = useState(false);
  // The comment currently being edited inline (its raw Greenlight id).
  const [editingId, setEditingId] = useState<string | null>(null);
  // Bumped after a top-level comment posts, to remount (clear) the composer.
  const [composerKey, setComposerKey] = useState(0);

  const { data: detail, isPending } = useQuery(taskDetailOptions(taskId));
  const loading = taskId != null && isPending;

  // The expanded subtask's markdown, fetched (and cached) on demand.
  const { data: expandedSubDesc, isPending: subDescPending } = useQuery({
    ...subtaskDescriptionOptions(expandedSub),
    enabled: expandedSub != null,
  });

  // --- Conversation rail: open at the newest comment, with scroll-cue chrome --
  const railRef = useRef<HTMLDivElement | null>(null);
  const railContentRef = useRef<HTMLDivElement | null>(null);
  // True while the rail is at (or near) the bottom — drives chat-style stickiness
  // when the thread grows.
  const atBottomRef = useRef(true);
  // One-shot guard so a background refetch (focus/poll) doesn't yank a reader who
  // scrolled up back down; re-armed when the modal closes.
  const scrolledTaskRef = useRef<string | null>(null);
  const [railOverflow, setRailOverflow] = useState({ up: false, down: false });
  const hasDetail = detail != null;

  const recomputeRailOverflow = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const up = el.scrollTop > 1;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    atBottomRef.current = !down;
    setRailOverflow((p) => (p.up === up && p.down === down ? p : { up, down }));
  }, []);

  // Drop the conversation to the newest comment when a task opens (the composer
  // is pinned below it; comments run oldest→newest). One-shot per open.
  useLayoutEffect(() => {
    if (taskId == null) {
      scrolledTaskRef.current = null;
      return;
    }
    const el = railRef.current;
    if (!el || !hasDetail || scrolledTaskRef.current === taskId) return;
    el.scrollTop = el.scrollHeight;
    scrolledTaskRef.current = taskId;
    recomputeRailOverflow();
  }, [taskId, hasDetail, recomputeRailOverflow]);

  // Keep the fade + "Earlier" cues accurate as the thread height changes (images
  // loading, replies expanding); re-pin to bottom only if already there.
  useEffect(() => {
    if (!hasDetail) return;
    const content = railContentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      const el = railRef.current;
      if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
      recomputeRailOverflow();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [hasDetail, recomputeRailOverflow]);

  const railMask = railFadeMask(railOverflow);

  const task = detail?.task;
  const canAct = canDecide && task?.signoffStatus === "pending_review";
  const canReopen =
    canDecide &&
    (task?.signoffStatus === "accepted" ||
      task?.signoffStatus === "changes_requested");

  // Feedback from every "request changes" round (newest first). A task can be
  // kicked back, fixed, re-projected, and kicked back again, so multiple rounds
  // accumulate as kind="feedback" comments. We surface the latest prominently
  // with older rounds collapsed, and keep the section visible after re-review/
  // acceptance so reviewers retain the context of what was asked.
  const feedbackRounds = [...(detail?.comments ?? [])]
    .filter((c) => c.kind === "feedback")
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  const latestFeedback = feedbackRounds[0] ?? null;
  const earlierFeedback = feedbackRounds.slice(1);
  // The latest round is "live" only while the task is still in changes_requested
  // — that's when it's editable and styled as an active alert.
  const feedbackActive = task?.signoffStatus === "changes_requested";

  // A decision changes the board, this task's detail, and notification feeds.
  function invalidateAfterDecision(id: string) {
    void queryClient.invalidateQueries({ queryKey: qk.board });
    void queryClient.invalidateQueries({ queryKey: [...qk.task, id] });
    void queryClient.invalidateQueries({ queryKey: qk.notifications });
  }

  // Optimistically drop a just-decided card from a column so it disappears the
  // instant the modal closes; the invalidation above reconciles both columns.
  // (The matching logic lives in dropTaskFromBoardColumns so the board key shape
  // is handled in one place and unit-tested.)
  function removeFromColumn(status: SignoffStatus, id: string) {
    dropTaskFromBoardColumns(queryClient, status, id);
  }

  const reopen = useMutation({
    mutationFn: (id: string) => reopenTask(id),
    onSuccess: (_r, id) => {
      toast.success("Moved back to review", {
        icon: <RotateCcw className="size-4" />,
      });
      onOpenChange(false); // close, like the other decisions (no-op for the toast Undo path)
      invalidateAfterDecision(id);
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't move it back");
    },
  });

  // Review-priority flag (dev/admin). Optimistic so the marks respond instantly;
  // invalidate the board afterwards so the card sort + indicator catch up.
  const priority = useMutation({
    mutationFn: (level: number) => setReviewPriority(taskId as string, level),
    onMutate: (level) => {
      if (!taskId) return;
      const key = [...qk.task, taskId];
      const prev = queryClient.getQueryData<TaskDetailData>(key);
      if (prev) {
        queryClient.setQueryData<TaskDetailData>(key, {
          ...prev,
          task: { ...prev.task, reviewPriority: level },
        });
      }
      return { key, prev };
    },
    onError: (_e, _level, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(ctx.key, ctx.prev);
      toast.error("Couldn't update priority");
    },
    onSettled: () => {
      if (taskId)
        void queryClient.invalidateQueries({ queryKey: [...qk.task, taskId] });
      void queryClient.invalidateQueries({ queryKey: qk.board });
    },
  });

  // Manual archive/unarchive from the ⋯ menu. Archiving closes the modal and
  // drops the card off the board; the toast offers Undo.
  const unarchive = useMutation({
    mutationFn: (id: string) => unarchiveTask(id),
    onSuccess: (_r, id) => {
      toast.success("Task restored to review", {
        icon: <RotateCcw className="size-4" />,
      });
      onOpenChange(false);
      invalidateAfterArchiveChange(queryClient, id);
    },
    onError: () => toast.error("Couldn't restore the task"),
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveTask(id),
    onSuccess: (_r, id) => {
      toast.success("Task archived", {
        icon: <Archive className="size-4" />,
        action: { label: "Undo", onClick: () => unarchive.mutate(id) },
      });
      onOpenChange(false);
      invalidateAfterArchiveChange(queryClient, id);
    },
    onError: () => toast.error("Couldn't archive the task"),
  });

  const accept = useMutation({
    mutationFn: (id: string) => acceptTask(id),
    onSuccess: (_r, id) => {
      toast.success("Task accepted", {
        icon: <CircleCheck className="size-4" />,
        description: "Synced to ClickUp.",
        action: { label: "Undo", onClick: () => reopen.mutate(id) },
      });
      setAction(null);
      onOpenChange(false);
      removeFromColumn("pending_review", id);
      invalidateAfterDecision(id);
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't accept the task");
    },
  });

  const requestChangesMutation = useMutation({
    mutationFn: (vars: {
      id: string;
      feedback: string;
      rich: RichDoc | null;
      attachmentIds: string[];
    }) =>
      requestChanges(vars.id, vars.feedback, {
        rich: vars.rich,
        attachmentIds: vars.attachmentIds,
      }),
    onSuccess: (_r, vars) => {
      toast.success("Changes requested", {
        icon: <MessageSquareText className="size-4" />,
        description: "Synced to ClickUp.",
        action: { label: "Undo", onClick: () => reopen.mutate(vars.id) },
      });
      setAction(null);
      onOpenChange(false);
      removeFromColumn("pending_review", vars.id);
      invalidateAfterDecision(vars.id);
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't send the task back");
    },
  });

  // Posting a comment or a reply (parentId set) — both refresh the detail's
  // comment thread on success.
  const postComment = useMutation({
    mutationFn: (vars: {
      body: string;
      parentId: string | null;
      rich: RichDoc | null;
      attachmentIds: string[];
    }) =>
      addTaskComment(taskId as string, vars.body, vars.parentId, {
        rich: vars.rich,
        attachmentIds: vars.attachmentIds,
      }),
    onSuccess: (_r, vars) => {
      if (vars.parentId) {
        setReplyingTo(null); // unmounts the reply composer → clears its draft
        toast.success("Reply added", {
          icon: <Reply className="size-4" />,
          description: "Synced to ClickUp.",
        });
      } else {
        setComposerKey((k) => k + 1); // remount the main composer → clears it
        toast.success("Comment added", {
          icon: <MessageSquare className="size-4" />,
          description: "Synced to ClickUp.",
        });
      }
      if (taskId) {
        void queryClient.invalidateQueries({ queryKey: [...qk.task, taskId] });
      }
    },
    onError: (e, vars) => {
      if (!handledClickupAuth(e))
        toast.error(
          vars.parentId ? "Couldn't add the reply" : "Couldn't add the comment",
        );
    },
  });

  // Edit your own comment / kickback feedback. Refreshes the detail thread and
  // the board (the card shows the latest feedback body).
  const editMut = useMutation({
    mutationFn: (vars: {
      commentId: string;
      body: string;
      rich: RichDoc | null;
      attachmentIds: string[];
    }) =>
      editTaskComment(taskId as string, vars.commentId, vars.body, {
        rich: vars.rich,
        attachmentIds: vars.attachmentIds,
      }),
    onSuccess: () => {
      setEditingId(null);
      toast.success("Comment updated", {
        icon: <Pencil className="size-4" />,
      });
      if (taskId) {
        void queryClient.invalidateQueries({ queryKey: [...qk.task, taskId] });
      }
      void queryClient.invalidateQueries({ queryKey: qk.board });
    },
    onError: (e) => {
      if (!handledClickupAuth(e)) toast.error("Couldn't update the comment");
    },
  });

  function startEdit(commentId: string) {
    setEditingId(commentId);
  }
  function cancelEdit() {
    setEditingId(null);
  }

  // Inline rich editor, shared by the feedback section and comment cards. Seeded
  // with the comment's existing rich doc (or plain body for legacy/ClickUp text).
  const editorBox = (c: {
    commentId: string;
    body: string;
    rich: RichDoc | null;
  }) => (
    <InlineComposer
      taskId={taskId as string}
      placeholder="Edit…"
      submitting={editMut.isPending}
      autoFocus
      submitLabel="Save"
      initialValue={c.rich ?? c.body}
      initialText={c.body}
      onCancel={cancelEdit}
      onSubmit={(p) =>
        editMut.mutate({
          commentId: c.commentId,
          body: p.body,
          rich: p.rich,
          attachmentIds: p.attachmentIds,
        })
      }
    />
  );

  function handleAccept() {
    if (taskId) accept.mutate(taskId);
  }

  function handleReopen() {
    if (taskId) reopen.mutate(taskId);
  }

  function toggleSubtask(s: ClickUpSubtask) {
    setExpandedSub((cur) => (cur === s.id ? null : s.id));
  }

  return (
    <>
      <Dialog open={!!taskId} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="flex h-[95vh] max-h-[68rem] min-h-[52rem] flex-col gap-0 overflow-hidden p-0 sm:max-w-7xl"
        >
          {/* Top-right actions: review-priority flag + ⋯ menu (reviewers) + close. */}
          <div className="absolute right-4 top-4 z-20 flex items-center gap-1">
            {task &&
              taskId &&
              (canFlag ? (
                <ReviewPriority
                  value={task.reviewPriority}
                  onChange={(level) => priority.mutate(level)}
                />
              ) : (
                <ReviewPriorityBadge level={task.reviewPriority} />
              ))}
            {canDecide && task && taskId && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Task actions"
                    className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-70 transition hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:opacity-100"
                  >
                    <MoreHorizontal className="size-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {task.signoffStatus === "archived" ? (
                    <DropdownMenuItem
                      onClick={() => taskId && unarchive.mutate(taskId)}
                    >
                      <RotateCcw className="size-4" />
                      Restore to review
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={() => taskId && archive.mutate(taskId)}
                    >
                      <Archive className="size-4" />
                      Archive
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DialogClose
              aria-label="Close"
              className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground opacity-70 transition hover:bg-muted hover:text-foreground hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </DialogClose>
          </div>

          {/* Header */}
          <DialogHeader className="space-y-1.5 border-b border-border px-7 py-5 text-left">
            {task && (
              <div className="flex items-center gap-2">
                {task.signoffStatus === "archived" ? (
                  <Archive className="size-4 text-muted-foreground" />
                ) : (
                  <img
                    src={STOPLIGHT_SVG[task.signoffStatus]}
                    alt=""
                    aria-hidden="true"
                    className="-ml-[5px] size-5"
                  />
                )}
                <span className="text-sm font-medium text-muted-foreground">
                  {STATUS_LABEL[task.signoffStatus]}
                </span>
              </div>
            )}
            <DialogTitle className="pr-6 text-base font-semibold leading-snug">
              {loading && !detail
                ? "Loading…"
                : task
                  ? (task.title ?? "Untitled task")
                  : "Task not found"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Task detail
            </DialogDescription>
          </DialogHeader>

          {/* Body row — scrollable main column + a ClickUp comments side rail,
              mirroring ClickUp's task view. Fixed dims so it never resizes
              between the loading and loaded states. */}
          <div className="flex min-h-0 flex-1">
            <div className="flex-1 overflow-y-auto px-7 py-6">
              {loading && !detail ? (
                <div className="flex h-full items-center justify-center">
                  <StoplightLoader
                    svgClassName="h-16 w-auto"
                    showWordmark={false}
                  />
                </div>
              ) : !task ? (
                <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  This task couldn't be loaded.
                </p>
              ) : (
                <div className="space-y-7">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                    {/* People */}
                    <Prop icon={UserRound} label="Requested by">
                      <Person
                        name={task.clickupCreatorName}
                        avatar={task.clickupCreatorAvatar}
                        initials={task.clickupCreatorInitials}
                      />
                    </Prop>
                    <Prop
                      icon={CircleUserRound}
                      label={
                        task.clickupAssignees.length > 1
                          ? "Assignees"
                          : "Assignee"
                      }
                    >
                      {task.clickupAssignees.length > 0 ? (
                        <div className="flex flex-col gap-1.5">
                          {task.clickupAssignees.map((a) => (
                            <Person
                              key={a.id}
                              name={a.name}
                              avatar={a.avatar}
                              initials={a.initials}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          Unassigned
                        </span>
                      )}
                    </Prop>
                    {/* Workflow state */}
                    <Prop icon={CircleDot} label="Status">
                      <ClickUpStatusPill
                        status={task.clickupStatus}
                        color={task.clickupStatusColor}
                      />
                    </Prop>
                    <Prop icon={CalendarCheck} label="Completed">
                      {fmtDate(task.enteredCompleteAt)}
                    </Prop>
                    <Prop icon={Flag} label="Priority">
                      <Priority
                        name={task.clickupPriority}
                        color={task.clickupPriorityColor}
                      />
                    </Prop>
                    <Prop icon={CalendarClock} label="Due">
                      {fmtDate(task.clickupDueDate)}
                    </Prop>
                    {/* Timeline */}
                    <Prop icon={Calendar} label="Created">
                      {fmtDate(task.clickupDateCreated)}
                    </Prop>
                    <Prop icon={Clock} label="Last changed">
                      {task.statusChangedAt
                        ? `${daysSince(task.statusChangedAt)} ago`
                        : "—"}
                    </Prop>
                    {/* Classification */}
                    <Prop icon={FolderOpen} label="Project">
                      {task.clickupListName ?? "—"}
                    </Prop>
                    {task.clickupTags && task.clickupTags.length > 0 && (
                      <Prop icon={Tag} label="Tags">
                        <div className="flex flex-wrap gap-1.5">
                          {task.clickupTags.map((tag) => (
                            <TagChip key={tag.name} tag={tag} />
                          ))}
                        </div>
                      </Prop>
                    )}
                  </div>

                  {task.signoffStatus === "accepted" && (
                    <section className="border-t border-border pt-5">
                      <div className="rounded-lg border border-signoff-green/30 bg-signoff-green/5 p-4">
                        <div className="flex items-center gap-2">
                          <CircleCheck className="size-3.5 shrink-0 text-signoff-green" />
                          <span className="text-sm font-semibold text-foreground">
                            Accepted
                          </span>
                          <span className="ml-auto shrink-0 text-right text-xs text-muted-foreground">
                            {task.reviewerName ?? "—"}
                            {task.decidedAt
                              ? ` · ${fmtDateTime(task.decidedAt)}`
                              : ""}
                          </span>
                        </div>
                      </div>
                    </section>
                  )}

                  {latestFeedback && (
                    <section className="border-t border-border pt-5">
                      <div className="rounded-lg border border-signoff-red/30 bg-signoff-red/5 p-4">
                        <div className="mb-2 flex items-center gap-2">
                          <MessageSquare className="size-3.5 shrink-0 text-signoff-red" />
                          <span className="text-sm font-semibold text-foreground">
                            Changes requested
                          </span>
                          <span className="ml-auto shrink-0 text-right text-xs text-muted-foreground">
                            {latestFeedback.authorName ?? "—"} ·{" "}
                            {fmtDateTime(latestFeedback.createdAt)}
                            {latestFeedback.editedAt ? " · edited" : ""}
                          </span>
                        </div>
                        {editingId === latestFeedback.id ? (
                          editorBox({
                            commentId: latestFeedback.id,
                            body: latestFeedback.body,
                            rich: latestFeedback.rich,
                          })
                        ) : (
                          <>
                            <RichCommentBody
                              rich={latestFeedback.rich}
                              fallback={latestFeedback.body}
                              attachments={latestFeedback.attachments}
                            />
                            {feedbackActive &&
                              latestFeedback.authorId === user.id && (
                                <div className="mt-2 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => startEdit(latestFeedback.id)}
                                    className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                  >
                                    <Pencil className="size-3.5" />
                                    Edit
                                  </button>
                                </div>
                              )}
                          </>
                        )}

                        {earlierFeedback.length > 0 && (
                          <div className="mt-3 border-t border-border/60 pt-2">
                            <button
                              type="button"
                              onClick={() => setShowEarlierFeedback((v) => !v)}
                              className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            >
                              <ChevronDown
                                className={`size-3.5 transition-transform ${
                                  showEarlierFeedback ? "" : "-rotate-90"
                                }`}
                              />
                              {earlierFeedback.length} earlier request
                              {earlierFeedback.length > 1 ? "s" : ""}
                            </button>
                            {showEarlierFeedback && (
                              <ul className="mt-1 flex flex-col divide-y divide-border/40">
                                {earlierFeedback.map((f) => (
                                  <li key={f.id} className="py-2 first:pt-1">
                                    <div className="mb-1 text-right text-xs text-muted-foreground">
                                      {f.authorName ?? "—"} ·{" "}
                                      {fmtDateTime(f.createdAt)}
                                      {f.editedAt ? " · edited" : ""}
                                    </div>
                                    <RichCommentBody
                                      rich={f.rich}
                                      fallback={f.body}
                                      attachments={f.attachments}
                                    />
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  <section className="border-t border-border pt-5">
                    {detail!.clickupMarkdown ? (
                      <Description markdown={detail!.clickupMarkdown} />
                    ) : task.description ? (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {task.description}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No description in ClickUp.
                      </p>
                    )}
                  </section>

                  {detail!.clickupAttachments &&
                    detail!.clickupAttachments.length > 0 && (
                    <section className="border-t border-border pt-6">
                      <SectionHeading>
                        Attachments ({detail!.clickupAttachments.length})
                      </SectionHeading>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {detail!.clickupAttachments.map((a) =>
                          a.isImage ? (
                            <ImageThumb
                              key={a.id}
                              thumbSrc={a.thumbnail ?? a.url}
                              fullSrc={a.url}
                              alt={a.title ?? "Attachment"}
                            />
                          ) : (
                            <a
                              key={a.id}
                              href={a.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground no-underline hover:bg-muted"
                            >
                              <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
                              <span className="truncate">
                                {a.title ?? "Attachment"}
                              </span>
                            </a>
                          ),
                        )}
                      </div>
                    </section>
                  )}

                  {detail!.clickupSubtasks.length > 0 && (
                    <section className="border-t border-border pt-6">
                      <SectionHeading>
                        Subtasks ({detail!.clickupSubtasks.length})
                      </SectionHeading>
                      <ul className="flex flex-col">
                        {detail!.clickupSubtasks.map((s) => {
                          const done =
                            s.statusType === "closed" ||
                            s.statusType === "done";
                          const open = expandedSub === s.id;
                          return (
                            <li
                              key={s.id}
                              className="border-b border-border/50 last:border-0"
                            >
                              <button
                                type="button"
                                onClick={() => toggleSubtask(s)}
                                className="flex w-full cursor-pointer items-center gap-2.5 py-2 text-left text-sm"
                              >
                                {done ? (
                                  <CircleCheck
                                    className="size-4 shrink-0"
                                    style={{
                                      color: s.statusColor ?? "#10CE63",
                                    }}
                                  />
                                ) : (
                                  <span
                                    aria-hidden="true"
                                    title={s.status ?? undefined}
                                    className="size-3.5 shrink-0 rounded-full border-2"
                                    style={{
                                      borderColor:
                                        s.statusColor ??
                                        "hsl(var(--muted-foreground))",
                                    }}
                                  />
                                )}
                                <span
                                  className={`flex-1 truncate ${done ? "text-muted-foreground line-through" : ""}`}
                                >
                                  {s.name}
                                </span>
                                {s.priority && (
                                  <Flag
                                    className="size-3.5 shrink-0 fill-current"
                                    style={{
                                      color:
                                        s.priorityColor ??
                                        PRIORITY_FALLBACK[
                                          s.priority.toLowerCase()
                                        ] ??
                                        "currentColor",
                                    }}
                                  />
                                )}
                                {s.assigneeName && (
                                  <Avatar
                                    className="size-5 shrink-0"
                                    title={s.assigneeName}
                                  >
                                    {s.assigneeAvatar && (
                                      <AvatarImage
                                        src={s.assigneeAvatar}
                                        alt={s.assigneeName}
                                      />
                                    )}
                                    <AvatarFallback className="text-[9px]">
                                      {s.assigneeInitials ?? s.assigneeName[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                )}
                                <ChevronDown
                                  className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`}
                                />
                              </button>

                              {open && (
                                <div className="mb-2 ml-6 flex flex-col gap-2 rounded-md bg-muted/40 p-3">
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                                    {s.status && (
                                      <span
                                        className="rounded px-1.5 py-0.5 font-medium uppercase tracking-wide"
                                        style={{
                                          color: s.statusColor ?? undefined,
                                          backgroundColor: s.statusColor
                                            ? `${s.statusColor}26`
                                            : undefined,
                                        }}
                                      >
                                        {s.status}
                                      </span>
                                    )}
                                    {s.dueDate && (
                                      <span className="flex items-center gap-1">
                                        <CalendarClock className="size-3.5" />
                                        {fmtDate(s.dueDate)}
                                      </span>
                                    )}
                                    {s.tags.map((tag) => (
                                      <TagChip key={tag.name} tag={tag} />
                                    ))}
                                  </div>

                                  {subDescPending ? (
                                    <p className="text-xs text-muted-foreground">
                                      Loading…
                                    </p>
                                  ) : expandedSubDesc ? (
                                    <Description markdown={expandedSubDesc} />
                                  ) : (
                                    <p className="text-xs text-muted-foreground">
                                      No description.
                                    </p>
                                  )}

                                  {s.url && (
                                    <a
                                      href={s.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                                    >
                                      <ExternalLink className="size-3.5" />
                                      Open in ClickUp
                                    </a>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  )}
                </div>
              )}
            </div>

            {/* Activity — side rail, like ClickUp's. ClickUp comments and task
                change events interleaved into one chronological feed.
                Greenlight's own sign-off comments stay in the main column. */}
            {task &&
              (() => {
                const { events, comments } = buildActivity(detail!);
                const renderEvent = (ev: ActivityEvent) => (
                  <li key={ev.id} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span
                        aria-hidden="true"
                        className="mt-1.5 size-1.5 shrink-0 rounded-full"
                        style={{
                          backgroundColor:
                            SIGNOFF_DOT[ev.toStatus ?? ""] ?? "#8B8B8B",
                        }}
                      />
                      <span className="flex-1 text-foreground">{ev.text}</span>
                      <span className="shrink-0 whitespace-nowrap">
                        {fmtDateTime(ev.date)}
                      </span>
                    </div>
                  </li>
                );
                const renderCard = (c: ActivityComment, isReply: boolean) => {
                  const canEdit = !!c.commentId && c.authorId === user.id;
                  const isEditing = !!c.commentId && editingId === c.commentId;
                  return (
                    <div className="rounded-lg border border-border/70 bg-card/60 p-3">
                      <div className="mb-1.5 flex items-center gap-2">
                        <Avatar className="size-6 shrink-0">
                          {c.authorAvatar && (
                            <AvatarImage
                              src={c.authorAvatar}
                              alt={c.authorName ?? ""}
                            />
                          )}
                          <AvatarFallback className="text-[10px]">
                            {c.authorInitials ?? c.authorName?.[0] ?? "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="truncate text-sm font-medium text-foreground">
                          {c.authorName ?? "—"}
                        </span>
                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                          {fmtDateTime(c.date)}
                          {c.editedAt ? " · edited" : ""}
                        </span>
                      </div>
                      <div className="text-sm text-foreground/90">
                        {isEditing ? (
                          editorBox({
                            commentId: c.commentId!,
                            body: c.text,
                            rich: c.rich,
                          })
                        ) : c.commentId ? (
                          // Greenlight comment: rich doc + its attachments.
                          <RichCommentBody
                            rich={c.rich}
                            fallback={c.text}
                            attachments={c.attachments}
                          />
                        ) : (
                          // ClickUp-origin comment: its own rich fragments.
                          <CommentBody fragments={c.fragments} fallback={c.text} />
                        )}
                      </div>
                      {!isEditing && (canEdit || !isReply) && (
                        <div className="mt-2 flex items-center justify-end gap-3 border-t border-border/50 pt-2 text-xs text-muted-foreground">
                          {canEdit && (
                            <button
                              onClick={() => startEdit(c.commentId!)}
                              className="flex cursor-pointer items-center gap-1 hover:text-foreground"
                            >
                              <Pencil className="size-3.5" />
                              Edit
                            </button>
                          )}
                          {!isReply && (
                            <button
                              onClick={() => setReplyingTo(c.refId)}
                              className="flex cursor-pointer items-center gap-1 hover:text-foreground"
                            >
                              <Reply className="size-3.5" />
                              Reply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                };
                return (
                  <aside className="relative hidden w-96 shrink-0 flex-col border-l border-border bg-muted/20 sm:flex">
                    {railOverflow.up && (
                      <button
                        type="button"
                        onClick={() =>
                          railRef.current?.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        className="absolute left-1/2 top-2 z-10 flex -translate-x-1/2 cursor-pointer items-center gap-1 rounded-full border border-border bg-background/90 px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur transition hover:bg-muted hover:text-foreground"
                        aria-label="Scroll to earlier activity"
                      >
                        <ChevronUp className="size-3.5" />
                        Earlier
                      </button>
                    )}
                    <div
                      ref={railRef}
                      onScroll={recomputeRailOverflow}
                      style={{ maskImage: railMask, WebkitMaskImage: railMask }}
                      className="flex-1 overflow-y-auto px-6 py-6"
                    >
                      <div ref={railContentRef}>
                      {/* Activity — the sign-off lifecycle events */}
                      {events.length > 0 && (
                        <div className="mb-6">
                          <SectionHeading>Activity</SectionHeading>
                          <ul className="flex flex-col gap-3">
                            {events.map(renderEvent)}
                          </ul>
                        </div>
                      )}

                      {/* Comments — the conversation */}
                      <div
                        className={
                          events.length > 0 ? "border-t border-border pt-6" : ""
                        }
                      >
                        <SectionHeading>Comments</SectionHeading>
                        {comments.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No comments yet.
                          </p>
                        ) : (
                          <ul className="flex flex-col gap-3">
                            {comments.map((node) => (
                              <li key={node.id} className="flex flex-col gap-2">
                                {renderCard(node, false)}
                                {node.replies.length > 0 && (
                                  <div className="ml-5 flex flex-col gap-2 border-l border-border/60 pl-3">
                                    {node.replies.map((r) => (
                                      <div key={r.id}>
                                        {renderCard(r, true)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {replyingTo === node.refId && (
                                  <div className="ml-5 pl-3">
                                    <InlineComposer
                                      taskId={taskId as string}
                                      placeholder="Write a reply…"
                                      submitting={
                                        postComment.isPending &&
                                        replyingTo === node.refId
                                      }
                                      autoFocus
                                      onCancel={() => setReplyingTo(null)}
                                      onSubmit={(p) =>
                                        postComment.mutate({
                                          body: p.body,
                                          parentId: node.refId,
                                          rich: p.rich,
                                          attachmentIds: p.attachmentIds,
                                        })
                                      }
                                    />
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      </div>
                    </div>
                    <div className="shrink-0 border-t border-border p-4">
                      <InlineComposer
                        key={commentComposerKey(taskId, composerKey)}
                        taskId={taskId as string}
                        placeholder="Add a comment…"
                        submitting={postComment.isPending && !replyingTo}
                        onSubmit={(p) =>
                          postComment.mutate({
                            body: p.body,
                            parentId: null,
                            rich: p.rich,
                            attachmentIds: p.attachmentIds,
                          })
                        }
                      />
                    </div>
                  </aside>
                );
              })()}
          </div>

          {/* Footer */}
          {task && (task.clickupUrl || canAct || canReopen) && (
            <DialogFooter className="flex-row items-center border-t border-border px-6 py-4 sm:justify-between">
              {task.clickupUrl ? (
                <a
                  href={task.clickupUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="size-4" />
                  Open in ClickUp
                </a>
              ) : (
                <span />
              )}
              {canAct ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setAction("request")}
                  >
                    Request changes
                  </Button>
                  <Button onClick={() => setAction("accept")}>Accept</Button>
                </div>
              ) : canReopen ? (
                <Button
                  variant="outline"
                  onClick={handleReopen}
                  disabled={reopen.isPending}
                  className="gap-2"
                >
                  {reopen.isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Moving…
                    </>
                  ) : (
                    "Move back to review"
                  )}
                </Button>
              ) : null}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <DecisionDialogs
        action={action}
        submitting={accept.isPending}
        taskTitle={task?.title ?? "this task"}
        onClose={() => setAction(null)}
        onAccept={handleAccept}
      />
      <RequestChangesDialog
        open={action === "request"}
        taskId={taskId}
        taskTitle={task?.title ?? "this task"}
        submitting={requestChangesMutation.isPending}
        onCancel={() => setAction(null)}
        onSubmit={(p) => {
          if (taskId) requestChangesMutation.mutate({ id: taskId, ...p });
        }}
      />
    </>
  );
}

export default TaskDetailDialog;
